import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withKeys as _withKeys, renderSlot as _renderSlot, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["title"]
const _hoisted_2 = { class: "navigation-sidebar__space-info" }
const _hoisted_3 = ["src", "alt"]
const _hoisted_4 = ["title"]
const _hoisted_5 = ["aria-hidden", "aria-label", "index", "onKeyup", "onClick"]
const _hoisted_6 = {
  key: 0,
  ref: "flyoutMenu",
  class: "navigation-sidebar-item__bottom-menu"
}
const _hoisted_7 = ["href", "aria-label", "aria-current", "aria-disabled", "onKeyup", "onClick"]
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { class: "flyout-menu-popup__content" }
const _hoisted_10 = { class: "flyout-menu-popup__content-link navigation-sidebar-item text-link" }
const _hoisted_11 = ["href"]
const _hoisted_12 = ["href", "title", "onKeyup", "onClick"]
const _hoisted_13 = ["title"]
const _hoisted_14 = {
  key: 0,
  class: "navigation-sidebar-item__submenu"
}
const _hoisted_15 = ["href"]
const _hoisted_16 = { class: "navigation-sidebar-item--flex-row" }
const _hoisted_17 = ["title"]

import { Drawer as KDrawer, DrawerContent as KDrawerContent } from '@progress/kendo-vue-layout';
import { Popup } from '@progress/kendo-vue-popup';
import { DrawerProps } from '@progress/kendo-vue-layout/dist/npm/drawer/interfaces/DrawerProps';
import { computed, ComputedRef, onMounted, ref, Ref, watch } from 'vue';
import { INavigationSidebarItem } from '@/types/navigationSidebarTypes';
import { Tooltip } from '@progress/kendo-vue-tooltip';
import config from '@/../service/project.config.js';
import { Skeleton } from '@progress/kendo-vue-indicators';
import { useAppStore } from '@/stores/app.store';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import helpers from '@/helpers';
import useTranslations from '@/composables/useTranslations';
import { useSpaceStore } from '@/stores/space.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { CwxBackend } from '@/types/cwxBackend';
import { animationSetting } from '@/constants/globalKendoConfig';
import ideaDefaultImage from '@/assets/images/defaults/space-default-image.png';


export default /*@__PURE__*/_defineComponent({
  __name: 'NavigationSidebar',
  props: {
  items: { type: Array<INavigationSidebarItem>, required: true },
  options: { type: Object, required: true },
},
  setup(__props) {

const props = __props;

const appStore = useAppStore();
const spaceStore = useSpaceStore();
const navigationStore = useNavigationStore();
const { dir } = storeToRefs(appStore);
const { translate: t } = useTranslations();
const route = useRoute();
const spaceImageRef = ref(null);
const isFlyoutMenuOpen = ref(false);
const options: Ref<DrawerProps> = ref(props.options);

const bottomItemsComputed: ComputedRef<INavigationSidebarItem[]> = computed(() => {
  return props.items.filter((item) => item.accessLevel?.startsWith('Admin'));
});
const drawerItems = computed(() => {
  const upperItems: INavigationSidebarItem[] = props.items.filter(
    (item) => !item.accessLevel?.startsWith('Admin'),
  );

  const footerMenu: INavigationSidebarItem = {
    id: 0,
    spaceId: { Value: 0 },
    text: '',
    tooltip: '',
    isAdminOnly: false,
    order: 0,
    url: '',
    type: CwxBackend.SpaceSidebarType.CustomMenuItem,
    accessLevel: CwxBackend.SpaceSidebarAccessLevel.Public,
    subType: 'footer',
    isOpen: false,
    selected: false,
    classes: 'bottom-menu',
    subItems: bottomSubmenuItems.value,
  };

  return [...upperItems, footerMenu];
});

const bottomSubmenuItems = computed(() => {
  const bottomItems: INavigationSidebarItem[] = bottomItemsComputed.value;

  bottomItems.forEach((item: INavigationSidebarItem) => {
    if (item.icon === 'icon-cog') item.ariaLabel = `${t('Settings')}`;
  });

  return [
    ...bottomItems,
    {
      ariaLabel: options.value.expanded
        ? `${t('Close')} ${t('Menu')}`
        : `${t('Common.Open')} ${t('Menu')}`,
      text: '',
      subType: 'toggle',
      icon: options.value.expanded ? 'icon-double-angle-left' : 'icon-double-angle-right',
      isOpen: false,
      selected: false,
      classes: '',
    },
  ] as INavigationSidebarItem[];
});

function itemClasses(item: INavigationSidebarItem) {
  let classes = 'navigation-sidebar-item';
  if (item?.classes) classes += ` ${item.classes}`;
  if (item?.type === CwxBackend.SpaceSidebarType.Separator)
    classes += ' k-drawer-item k-drawer-separator';
  if (item?.subItems && item?.subItems?.length > 0)
    classes += ' navigation-sidebar-item--with-submenu';

  const href = window?.location?.href;
  if (
    item?.isOpen ||
    (navigationStore.$state.sidebarNavigationMenuActiveOverride?.menuItemType &&
      navigationStore.$state.sidebarNavigationMenuActiveOverride?.menuItemType ===
        item?.menuItemType) ||
    (item?.url && href?.includes(item?.url)) ||
    (item?.url && item?.url?.includes('SubmitIdea') && href?.includes('draft'))
  ) {
    classes += ' active';
  }

  return classes;
}

function isHref(item: INavigationSidebarItem) {
  return !(
    (item?.isFlyout && spaceStore.spaceDetails.type === CwxBackend.SpaceType.Challenge) ||
    !item?.url
  );
}

function onItemClick(item: INavigationSidebarItem) {
  if (item.isFlyout && spaceStore.spaceDetails.type === CwxBackend.SpaceType.Challenge) {
    isFlyoutMenuOpen.value = !isFlyoutMenuOpen.value;
  } else if (item?.url) {
    window.location.href = config.backend + item?.url;
  } else if (item?.action) {
    item.action();
  }

  if (item?.subType === 'toggle') {
    options.value.expanded = !options.value.expanded;
  }

  if (typeof item.index === 'number') {
    drawerItems.value[item.index].isOpen = !drawerItems.value[item.index].isOpen;
    if (item?.subType === 'toggle')
      drawerItems.value[item.index].icon = options.value.expanded
        ? 'icon-double-angle-left'
        : 'icon-double-angle-right';
  }
  navigationStore.saveDrawerOptionsToLocalStorage(options.value.expanded === true);
}

function checkStateSidebarExpand() {
  if (props?.items?.length > 0) {
    document.body.classList.toggle('navigation-sidebar-expanded', options.value.expanded);
  }
}

function openChallengeModal() {
  isFlyoutMenuOpen.value = false;
  window.location.replace(
    `/Challenges/CreateChallenge?spaceId=${spaceStore.spaceDetails.id}&returnUrl=${route.fullPath}`,
  );
}

function getActiveIcon(item: INavigationSidebarItem) {
  return item?.props?.iconActive ?? `${item?.props?.icon}-active`;
}

onMounted(() => {
  checkStateSidebarExpand();
});

watch(options.value, () => {
  checkStateSidebarExpand();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(KDrawer), {
    class: _normalizeClass(["navigation-sidebar", {
      'navigation-sidebar--hidden': __props.items?.length === 0,
      'navigation-sidebar--mini': bottomItemsComputed.value.length > 1,
    }]),
    "data-testid": "space-sidebar",
    expanded: options.value.expanded,
    position: options.value.position,
    mode: options.value.mode,
    mini: options.value.mini,
    items: drawerItems.value,
    item: 'CustomItem',
    "mini-width": options.value.miniWidth,
    width: options.value.width,
    dir: _unref(dir),
    "navigation-header": 'headerTemplate'
  }, {
    headerTemplate: _withCtx(() => [
      (_unref(spaceStore)?.spaceDetails?.id > 0)
        ? (_openBlock(), _createBlock(_unref(Tooltip), {
            key: 0,
            filter: () => !options.value.expanded,
            "class-name": 'navigation-sidebar-tooltip',
            "open-delay": 0,
            position: 'right',
            "anchor-element": 'target',
            "parent-title": true
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                class: "navigation-sidebar__header",
                title: _unref(spaceStore)?.spaceDetails?.name
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("img", {
                    ref_key: "spaceImageRef",
                    ref: spaceImageRef,
                    src: 
                _unref(spaceStore)?.spaceDetails?.isSpaceImageExists
                  ? `/files/spaceimage?spaceId=${_unref(spaceStore)?.spaceDetails?.id}`
                  : _unref(ideaDefaultImage)
              ,
                    alt: _unref(t)('Space.Image'),
                    class: "navigation-sidebar__space-info-image"
                  }, null, 8, _hoisted_3),
                  (options.value.expanded)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "navigation-sidebar__space-info-name",
                        title: _unref(spaceStore)?.spaceDetails?.name
                      }, _toDisplayString(_unref(spaceStore)?.spaceDetails?.name), 9, _hoisted_4))
                    : _createCommentVNode("", true)
                ])
              ], 8, _hoisted_1)
            ]),
            _: 1
          }, 8, ["filter"]))
        : _createCommentVNode("", true)
    ]),
    CustomItem: _withCtx((customItemContext) => [
      (_unref(spaceStore)?.spaceDetails?.id == null || _unref(spaceStore)?.spaceDetails?.id > 0)
        ? (_openBlock(), _createElementBlock("li", {
            key: 0,
            role: "menuitem",
            "aria-current": "false",
            "aria-disabled": "false",
            "aria-hidden": 
          customItemContext?.props?.type === _unref(CwxBackend).SpaceSidebarType.Separator
            ? 'true'
            : 'false'
        ,
            "aria-label": `${_unref(t)('Common.Link')} ${customItemContext?.props?.text}`,
            class: _normalizeClass(itemClasses(customItemContext?.props)),
            tabindex: '-1',
            index: customItemContext?.props?.id,
            onKeyup: _withKeys(customItemContext?.props?.onClick, ["enter"]),
            onClick: customItemContext?.props?.onClick
          }, [
            (customItemContext?.props?.skeleton)
              ? (_openBlock(), _createBlock(_unref(Skeleton), {
                  key: 0,
                  shape: 'rectangle',
                  animation: { type: 'pulse' },
                  tabindex: "-1"
                }))
              : (_openBlock(), _createBlock(_unref(Tooltip), {
                  key: 1,
                  filter: () => !options.value.expanded,
                  "class-name": 'navigation-sidebar-tooltip',
                  "open-delay": 0,
                  position: 'right',
                  "anchor-element": 'target',
                  "parent-title": true
                }, {
                  default: _withCtx(() => [
                    (customItemContext?.props?.subType === 'footer')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customItemContext?.props?.subItems, (bottomMenuItem, bottomMenuItemIndex) => {
                            return (_openBlock(), _createElementBlock("a", {
                              key: bottomMenuItemIndex,
                              class: "navigation-sidebar-item",
                              tabindex: "0",
                              role: "button",
                              href: isHref(bottomMenuItem) ? _unref(config).backend + `${bottomMenuItem.url}` : undefined,
                              target: "_blank",
                              "aria-label": bottomMenuItem?.ariaLabel || bottomMenuItem?.text,
                              "aria-current": bottomMenuItem?.active ? 'true' : 'false',
                              "aria-disabled": bottomMenuItem?.disabled,
                              onKeyup: _withKeys(($event: any) => (isHref(bottomMenuItem) ? null : onItemClick(bottomMenuItem)), ["enter"]),
                              onClick: ($event: any) => (isHref(bottomMenuItem) ? null : onItemClick(bottomMenuItem))
                            }, [
                              (bottomMenuItem?.icon)
                                ? (_openBlock(), _createElementBlock("i", {
                                    key: 0,
                                    class: _normalizeClass(
                  'cursor-pointer navigation-sidebar-item__menu--icon cwx-icon cwx-icon-before_' +
                  bottomMenuItem?.icon
                )
                                  }, null, 2))
                                : _createCommentVNode("", true),
                              (bottomMenuItem.isFlyout)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                                    (bottomMenuItem.isFlyout)
                                      ? (_openBlock(), _createBlock(_unref(Popup), {
                                          key: 0,
                                          anchor: 'flyoutMenu',
                                          "class-name": 'flyout-menu-popup navigation-sidebar__flyout-menu',
                                          class: _normalizeClass('flyout-menu-popup navigation-sidebar__flyout-menu'),
                                          show: isFlyoutMenuOpen.value,
                                          "anchor-align": { horizontal: 'left', vertical: 'middle' },
                                          "popup-align": { horizontal: 'left', vertical: 'middle' },
                                          animate: _unref(animationSetting)
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_9, [
                                              _createElementVNode("div", {
                                                class: "flyout-menu-popup__content-link text-link",
                                                role: "button",
                                                tabindex: "0",
                                                onKeyup: _withKeys(openChallengeModal, ["enter"]),
                                                onClick: openChallengeModal
                                              }, _toDisplayString(_unref(t)('Settings')), 33),
                                              _createElementVNode("div", _hoisted_10, [
                                                _createElementVNode("a", {
                                                  href: 
                          _unref(helpers).url.outerUrl(
                            `/administration/spacehomesettings?spaceID=${_unref(spaceStore)?.spaceDetails?.id}`,
                          )
                        
                                                }, _toDisplayString(_unref(t)('ExpertSettings')), 9, _hoisted_11)
                                              ])
                                            ])
                                          ]),
                                          _: 1
                                        }, 8, ["show", "animate"]))
                                      : _createCommentVNode("", true)
                                  ]))
                                : _createCommentVNode("", true)
                            ], 40, _hoisted_7))
                          }), 128))
                        ], 512))
                      : (customItemContext?.props?.type !== _unref(CwxBackend).SpaceSidebarType.Separator)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 1,
                            class: "navigation-sidebar-item__top-menu",
                            tabindex: "0",
                            href: 
              isHref(customItemContext?.props)
                ? _unref(config).backend + `${customItemContext?.props.url}`
                : undefined
            ,
                            title: customItemContext?.props?.text,
                            onKeyup: _withKeys(($event: any) => (onItemClick(customItemContext?.props)), ["enter"]),
                            onClick: ($event: any) => (onItemClick(customItemContext?.props))
                          }, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["navigation-sidebar-item--flex-row", {
                expanded:
                  customItemContext?.props?.isOpen &&
                  customItemContext?.props?.subItems?.length > 0,
              }])
                            }, [
                              (customItemContext?.props?.icon)
                                ? (_openBlock(), _createElementBlock("i", {
                                    key: 0,
                                    class: _normalizeClass([
                  customItemContext?.props?.selected
                    ? `cursor-pointer navigation-sidebar-item__menu--icon cwx-old-icon ${customItemContext?.props?.icon}  cwx-icon-before_${getActiveIcon(
                        customItemContext,
                      )}`
                    : `cursor-pointer navigation-sidebar-item__menu--icon cwx-old-icon ${customItemContext?.props?.icon}  cwx-icon cwx-icon-before_${customItemContext?.props?.icon}`,
                ])
                                  }, null, 2))
                                : _createCommentVNode("", true),
                              (customItemContext?.props?.text && options.value.expanded)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 1,
                                    class: "navigation-sidebar-item__menu--label",
                                    title: customItemContext?.props?.text
                                  }, _toDisplayString(customItemContext?.props?.text), 9, _hoisted_13))
                                : _createCommentVNode("", true)
                            ], 2),
                            (
                customItemContext?.props?.subItems?.length > 0 && customItemContext?.props?.isOpen
              )
                              ? (_openBlock(), _createElementBlock("ul", _hoisted_14, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(customItemContext?.props?.subItems, (subItem, indexSubItem) => {
                                    return (_openBlock(), _createElementBlock("a", {
                                      key: indexSubItem,
                                      class: "k-drawer-item",
                                      href: subItem?.url ? _unref(config).backend + subItem?.url : '#'
                                    }, [
                                      _createElementVNode("li", _hoisted_16, [
                                        _createElementVNode("i", {
                                          class: _normalizeClass(["icon-large icon-circle", `submenu-color-${indexSubItem}`])
                                        }, null, 2),
                                        (
                      subItem?.text &&
                      options.value.expanded &&
                      subItem?.type !== _unref(CwxBackend).SpaceSidebarType.Separator
                    )
                                          ? (_openBlock(), _createElementBlock("div", {
                                              key: 0,
                                              class: "navigation-sidebar-item__submenu--label",
                                              title: subItem?.text
                                            }, _toDisplayString(subItem?.text), 9, _hoisted_17))
                                          : _createCommentVNode("", true)
                                      ])
                                    ], 8, _hoisted_15))
                                  }), 128))
                                ]))
                              : _createCommentVNode("", true)
                          ], 40, _hoisted_12))
                        : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["filter"]))
          ], 42, _hoisted_5))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(KDrawerContent), null, {
        default: _withCtx(() => [
          _renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      })
    ]),
    _: 3
  }, 8, ["expanded", "position", "mode", "mini", "items", "mini-width", "width", "class", "dir"]))
}
}

})